/* eslint-disable no-useless-computed-key */
import React, { useRef, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { Box, Grid } from '@mui/material';
import { useReservationOperations, useSelectorRef } from 'hooks';
import { DrawerLayout, TaskLayout } from '@layouts';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import InfoIcon from '@mui/icons-material/Info';
import HistoryIcon from '@mui/icons-material/History';
import {
  ActivityListV2,
  CommentsV2,
  ReservationForm,
  TabsV2,
  TaskDetailScrollableSection,
  TaskSection,
} from '@components';
import { ButtonV2 } from 'components/common/ui';
import { showError } from '@utils';
import { ENTITY_TYPES_COMMENTS } from '@constants';
import { reservationServices } from 'services/reservations';
import { ENTITIES_PERMISIONS } from 'components/common/PermissionsGate/constants';
import { selectLoginState } from '@store';
import { formatISO } from 'date-fns';

export const tabs = [
  {
    label: 'Ticket Details',
    id: 'ticket-details',
    icon: <InfoIcon data-testid="InfoIcon" />,
  },
  {
    label: 'Comments',
    id: 'comments',
    icon: <ChatBubbleIcon data-testid="ChatBubbleIcon" />,
  },
  {
    label: 'History',
    id: 'history',
    icon: <HistoryIcon data-testid="HistoryIcon" />,
  },
];

const Reservation = () => {
  const {
    getReservation,
    reservation,
    reservationId,
    reservationsTypes,
    loading,
    agents,
    history,
    getHistory,
    mentionedUsers,
  } = useReservationOperations();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const router = useRouter();
  const submitBtnRef = useRef();

  const oauth = useSelectorRef(selectLoginState);
  const loggedInUser = oauth?.user;

  const handleDelete = async () => {
    setIsSubmitting(true);

    try {
      const payload = {
        isActive: false,
      };

      await reservationServices.updateReservation(reservationId, payload);
      router?.push('/reservations');
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (values) => {
    delete values.createdAt;
    delete values.updatedAt;

    if (values?.promisedDeliveryDate?.length) {
      const [promisedDeliveryDate, promisedDeliveryDateUntil] =
        values.promisedDeliveryDate;
      values.promisedDeliveryDate = promisedDeliveryDate
        ? formatISO(promisedDeliveryDate)
        : null;
      values.promisedDeliveryDateUntil = promisedDeliveryDateUntil
        ? formatISO(promisedDeliveryDateUntil)
        : null;
    } else {
      values.promisedDeliveryDate = null;
      values.promisedDeliveryDateUntil = null;
    }

    try {
      setIsSubmitting(true);
      await reservationServices.updateReservation(reservationId, values);
      await getReservation();
      await getHistory();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddComment = async (comment) => {
    try {
      setIsSubmitting(true);
      // we need to submit the form every time we save the comments
      await submitBtnRef?.current?.click?.();
      const payload = {
        entityID: reservationId,
        entityType: ENTITY_TYPES_COMMENTS.reservations,
        comment,
      };

      await reservationServices.addReservationComment(payload);

      await getReservation();
      await getHistory();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditComment = async (commentId, comment) => {
    try {
      setIsSubmitting(true);
      // we need to submit the form every time we edit the comments
      await submitBtnRef?.current?.click?.();

      const payload = {
        comment,
      };

      await reservationServices.editReservationComment(commentId, payload);
      await getReservation();
      await getHistory();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      setIsSubmitting(true);
      // we need to submit the form every time we delete the comments
      await submitBtnRef?.current?.click?.();
      await reservationServices.deleteReservationComment(commentId);
      await getReservation();
      await getHistory();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderHeaderOptions = () => {
    return (
      <Box sx={{ display: 'flex', gap: '8px' }}>
        {reservation?.type !== 4 && ( // not Booked
          <ButtonV2 variant="outlined" onClick={handleDelete}>
            Delete
          </ButtonV2>
        )}
        <ButtonV2
          ref={submitBtnRef}
          type="submit"
          form="reservation-form"
          disabled={isSubmitting}
        >
          Save
        </ButtonV2>
      </Box>
    );
  };

  return (
    <>
      <Head>
        <title>{`Reservation ID: ${reservationId}`}</title>
        <meta name="description" content="Instacar ledger" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <TaskLayout
        task={reservationId}
        entity={ENTITIES_PERMISIONS.ReadReservations}
        renderHeaderOptions={renderHeaderOptions}
        isLoading={loading || isSubmitting}
      >
        <Grid container alignItems="start" spacing={1}>
          <Grid size={12}>
            <TabsV2 tabs={tabs} observerRootId="reservations-task-details" />
            <TaskDetailScrollableSection id="reservations-task-details">
              <ReservationForm
                defaultValues={reservation}
                agentsOptions={agents}
                reservationsOptions={reservationsTypes}
                loading={loading || isSubmitting}
                onSubmit={handleSubmit}
                loggedInUser={loggedInUser}
              />
              <TaskSection title="Comments" id={tabs[1].id} icon={tabs[1].icon}>
                <CommentsV2
                  comments={reservation?.comments}
                  onSubmit={handleAddComment}
                  onEdit={handleEditComment}
                  onDelete={handleDeleteComment}
                  users={mentionedUsers}
                />
              </TaskSection>
              <TaskSection title="History" id={tabs[2].id} icon={tabs[2].icon}>
                <ActivityListV2 activities={history} />
              </TaskSection>
            </TaskDetailScrollableSection>
          </Grid>
        </Grid>
      </TaskLayout>
    </>
  );
};

Reservation.getLayout = (page) => {
  return <DrawerLayout>{page}</DrawerLayout>;
};

export default Reservation;
